import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useNetworkState } from '../hooks/useNetworkState';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, User, Settings, LogOut, Package, Users, MapPin, ShoppingCart, BarChart2 } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import NotificationsDropdown from '../components/NotificationsDropdown';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useQueryClient } from '@tanstack/react-query';
import api from '../api/axios';
import toast from 'react-hot-toast';

import { EndDayModal } from '../components/attendance/EndDayModal';
import { getMediaUrl } from '../utils/url';
import { OfflineSyncManager } from '../components/mobile/OfflineSyncManager';

export const MobileLayout = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();
  const { isOnline } = useNetworkState();

  const [isEndDayModalOpen, setIsEndDayModalOpen] = useState(false);

  // Push Notifications Initialization
  // Capacitor has been removed. Web push will be implemented later if needed.
  useEffect(() => {
    // Desktop/web push notifications logic can be added here
  }, [queryClient]);

  // Предзагрузка данных при наведении на таб — страница открывается быстрее
  const handlePrefetch = (path: string) => {
    if (path === '/agent/stocks') {
      queryClient.prefetchQuery({
        queryKey: ['agent-stocks'],
        queryFn: async () => {
          const res = await api.get('/stock');
          return Array.isArray(res.data) ? res.data : res.data?.data || [];
        },
        staleTime: 10 * 60 * 1000,
      });
    } else if (path === '/agent/clients') {
      queryClient.prefetchQuery({
        queryKey: ['clients'],
        queryFn: async () => {
          const res = await api.get('/clients');
          return res.data.data || res.data;
        },
        staleTime: 10 * 60 * 1000,
      });
    } else if (path === '/agent/dashboard') {
      queryClient.prefetchQuery({
        queryKey: ['agent-dashboard'],
        queryFn: async () => {
          const res = await api.get('/analytics/agent-dashboard');
          return res.data;
        },
        staleTime: 3 * 60 * 1000,
      });
    }
  };

  const navItems = [
    { name: t('nav.dashboard'), icon: <LayoutDashboard size={24} />, path: '/agent/dashboard' },
    { name: t('nav.visits'), icon: <MapPin size={24} />, path: '/agent/visits' },
    { name: t('nav.orders'), icon: <ShoppingCart size={24} />, path: '/agent/orders' },
    { name: t('nav.clients'), icon: <Users size={24} />, path: '/agent/clients' },
    { name: t('nav.stocks'), icon: <Package size={24} />, path: '/agent/stocks' },
    { name: t('nav.analytics'), icon: <BarChart2 size={24} />, path: '/agent/analytics' },
  ];

  const getPageTitle = () => {
    const currentPath = location.pathname;
    const item = navItems.find((item) => item.path === currentPath);
    return item ? item.name : 'ERP Agent';
  };

  return (
    <div className="flex flex-col h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Mobile Header */}
      <header className="h-14 shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-bold truncate">{getPageTitle()}</h1>
        </div>
        <div className="flex items-center gap-3">
          <NotificationsDropdown />

          <Popover>
            <PopoverTrigger asChild>
              <button className="flex items-center justify-center w-8 h-8 rounded-full overflow-hidden hover:opacity-80 transition-opacity outline-hidden focus:ring-0 active:scale-95 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                {user?.photo_url ? (
                  <img src={getMediaUrl(user.photo_url)} className="w-full h-full object-cover" alt="Avatar" />
                ) : (
                  <User size={18} className="text-slate-600 dark:text-slate-400" />
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-60 p-0 rounded-sm overflow-hidden border-slate-200 dark:border-slate-800 shadow-2xl"
              align="end"
              sideOffset={8}
            >
              <div className="p-1.5">
                <Link
                  to="/settings/profile"
                  className="flex items-center gap-3 px-3 py-2.5 w-full hover:bg-slate-100 dark:hover:bg-slate-800 rounded-sm transition-colors group text-slate-700 dark:text-slate-200"
                >
                  <Settings size={16} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
                  <span className="text-sm font-semibold">{t('agent.menu.settings')}</span>
                </Link>

                <button
                  onClick={() => logout()}
                  className="flex items-center gap-3 px-3 py-2.5 w-full hover:bg-red-50 dark:hover:bg-red-900/10 rounded-sm transition-colors group text-red-600 dark:text-red-400"
                >
                  <LogOut size={16} className="text-red-400 group-hover:text-red-600 transition-colors" />
                  <span className="text-sm font-semibold">{t('agent.menu.logout')}</span>
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </header>

      {!isOnline && (
        <div className="bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider text-center py-1.5 shadow-xs shrink-0 z-40 relative">
          Оффлайн режим — Данные сохраняются локально
        </div>
      )}

      <OfflineSyncManager />

      {/* Content Area */}
      <main className="flex-1 overflow-auto pb-20 p-4">
        <Outlet />
      </main>

      {/* Bottom Tab Bar */}
      <nav className="h-16 shrink-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex items-center justify-around px-2 fixed bottom-0 left-0 right-0 z-50">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onMouseEnter={() => handlePrefetch(item.path)}
              onTouchStart={() => handlePrefetch(item.path)}
              className={`flex flex-col items-center justify-center flex-1 h-full min-w-[48px] transition-colors ${
                isActive ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'
              }`}
            >
              {item.icon}
              <span className="text-[10px] font-medium mt-1 uppercase tracking-tight">{item.name}</span>
            </Link>
          );
        })}
      </nav>
      <EndDayModal
        isOpen={isEndDayModalOpen}
        onClose={() => setIsEndDayModalOpen(false)}
        onSuccess={() => {
          toast.success('Рабочий день успешно завершен!');
          window.location.reload(); // To lock screen again via CameraGeoGuard
        }}
      />
    </div>
  );
};
